import { useEffect, useMemo, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { api, parseErr } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/lib/auth';
import { useFeature } from '@/lib/features';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  CalendarDays, CheckCircle2, AlertTriangle, Flame, Move, Plus, CalendarClock,
  ArrowRight, TimerReset, Info, Layers, Flag,
} from 'lucide-react';
import { STAGE_LABEL, STAGES } from '@/lib/roles';
import { todayISO, addDaysISO, endOfThisWeekISO } from '@/lib/dates';
import { MyMilestonesStrip } from '@/components/rhythm/MyMilestonesStrip';

const LEAD_ROLES = ['owner', 'operations', 'dm_creative', 'dm_technical', 'dm_architecture'];

const BUCKETS = [
  { key: 'today',     label: 'Today',       hint: 'On your plate today — today or already overdue.', color: 'hsl(var(--danger))' },
  { key: 'this_week', label: 'This week',   hint: 'Target date within the next 7 days.',              color: 'hsl(var(--primary))' },
  { key: 'later',     label: 'Later',       hint: 'Beyond this week (or no date set).',                color: 'hsl(var(--foreground-muted))' },
];

// Capacity thresholds for the "heavy" badge — surfaces overload risk before
// the counter fires. `today` gets the tightest ceiling because those are due-now
// items; `this_week` gets a looser one; `later` is intentionally not gated.
const BUCKET_CAPACITY = { today: 3, this_week: 5, later: null };
function capacityFor(bucket, count) {
  const cap = BUCKET_CAPACITY[bucket];
  if (!cap || count <= cap) return null;
  const over = count - cap;
  if (bucket === 'today') {
    return {
      tone: 'danger',
      label: `Heavy · ${count} due`,
      hint: `More than ${cap} items landing today. Consider re-baselining with a lead before it becomes overload.`,
    };
  }
  return {
    tone: over >= 3 ? 'danger' : 'warning',
    label: `Heavy · ${count} open`,
    hint: `Carrying ${count} open commitments this bucket. Threshold is ${cap}.`,
  };
}

// Assign a commitment to a bucket by its days_until_target (undefined => later).
function bucketOf(c) {
  const d = c.days_until_target;
  if (c.derived_status === 'overdue') return 'today';
  if (d === null || d === undefined) return 'later';
  if (d <= 0) return 'today';
  if (d <= 7) return 'this_week';
  return 'later';
}

// When dragging to a bucket, choose a sensible new target date.
function targetForBucket(bucket) {
  if (bucket === 'today') return todayISO();
  if (bucket === 'this_week') return endOfThisWeekISO();
  return addDaysISO(14); // Later default: two weeks out
}

export default function MyWeekPage() {
  const { user } = useAuth();
  const enabled = useFeature('operating_rhythm_v4');
  const isLead = LEAD_ROLES.includes(user.role);
  const [data, setData] = useState(null);
  const [projects, setProjects] = useState([]);
  const [meta, setMeta] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [shiftFor, setShiftFor] = useState(null);
  const [pendingShifts, setPendingShifts] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get('new') === '1') {
      setAddOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete('new');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    try {
      const [c, m, p] = await Promise.all([
        api.get('/commitments/me?window=all'),
        api.get('/rhythm/meta'),
        api.get('/projects'),
      ]);
      setData(c.data);
      setMeta(m.data);
      setProjects(p.data || []);
      if (isLead) {
        try {
          const q = await api.get('/commitments/shifts/pending');
          setPendingShifts(q.data?.rows || []);
        } catch { setPendingShifts([]); }
      }
    } catch (e) { toast.error(parseErr(e)); }
  }, [isLead]);

  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => {
    if (!data) return { today: [], this_week: [], later: [] };
    const open = (data.commitments || []).filter(c => c.derived_status === 'open' || c.derived_status === 'overdue');
    const buckets = { today: [], this_week: [], later: [] };
    for (const c of open) buckets[bucketOf(c)].push(c);
    // Sort each bucket by target_date
    for (const k of Object.keys(buckets)) {
      buckets[k].sort((a, b) => (a.target_date || 'zzz').localeCompare(b.target_date || 'zzz'));
    }
    return buckets;
  }, [data]);

  const overloadCount = useMemo(() =>
    (data?.commitments || []).filter(c => c.overload_flag).length, [data]);

  const onDragStart = (e, c) => {
    if (c.derived_status === 'done' || c.derived_status === 'cancelled') return;
    e.dataTransfer.setData('text/plain', c.id);
    e.dataTransfer.effectAllowed = 'move';
  };
  const onDragOver = (e, bucket) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOver(bucket);
  };
  const onDragLeave = () => setDragOver(null);
  const onDrop = async (e, bucket) => {
    e.preventDefault();
    setDragOver(null);
    const cid = e.dataTransfer.getData('text/plain');
    if (!cid || !data) return;
    const c = data.commitments.find(x => x.id === cid);
    if (!c) return;
    if (bucketOf(c) === bucket) return; // No-op
    const newDate = targetForBucket(bucket);
    setBusyId(cid);
    try {
      await api.patch(`/commitments/${cid}`, { target_date: newDate });
      toast.success('Moved to ' + BUCKETS.find(b => b.key === bucket).label + '.');
      load();
    } catch (err) { toast.error(parseErr(err)); }
    finally { setBusyId(null); }
  };

  return (
    <AppShell breadcrumbs={[{ label: 'My Week' }]}>
      <div className="p-6 max-w-[1400px] mx-auto space-y-4 fade-up" data-testid="my-week-page">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-heading text-2xl font-semibold flex items-center gap-2">
              <CalendarDays size={22} />My Week
            </h1>
            <p className="text-[13px] text-[hsl(var(--foreground-muted))] mt-1 max-w-[720px]">
              Plan your next seven days. Drag a card between columns to change its target date,
              hit <b>Done</b> when it&rsquo;s finished, or <b>Shift</b> it with a quick reason when priorities change.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={() => setAddOpen(true)} data-testid="my-week-add-cta">
              <Plus size={14} className="mr-1" />New commitment
            </Button>
          </div>
        </div>

        {enabled === false && (
          <div className="card p-3 text-[13px] text-[hsl(var(--foreground-muted))]">
            Operating Rhythm v4 is not enabled for your role yet.
          </div>
        )}

        {overloadCount > 0 && (
          <div className="card p-3 border-l-4 flex items-start gap-2" style={{ borderLeftColor: 'hsl(var(--danger))', background: 'hsl(var(--danger-bg))' }} data-testid="my-week-overload-banner">
            <Flame size={16} className="mt-0.5" style={{ color: 'hsl(var(--danger))' }} />
            <div className="text-[13px]">
              <b>{overloadCount} commitment{overloadCount === 1 ? '' : 's'} flagged as overload.</b>{' '}
              You’ve moved these more than once. Consider asking a lead for a re-baseline instead of a self-move.
            </div>
          </div>
        )}

        {isLead && pendingShifts.length > 0 && (
          <div className="card p-3 border-l-4 flex flex-col gap-2" style={{ borderLeftColor: 'hsl(var(--warning))', background: 'hsl(var(--warning-bg))' }} data-testid="my-week-pending-shifts">
            <div className="flex items-center gap-2">
              <Info size={14} style={{ color: 'hsl(var(--warning))' }} />
              <div className="text-[13px]"><b>{pendingShifts.length} priority shift{pendingShifts.length === 1 ? '' : 's'} pending confirmation.</b> Confirm to re-baseline without KRA penalty.</div>
            </div>
            <ul className="divide-y" style={{ borderColor: 'hsl(var(--border))' }}>
              {pendingShifts.slice(0, 4).map(r => (
                <li key={r.commitment_id} className="py-1.5 text-[12.5px] flex items-center justify-between" data-testid={`pending-shift-${r.commitment_id}`}>
                  <div>
                    <b>{r.user_name}</b> · {r.title}
                    <span className="text-[hsl(var(--foreground-muted))] ml-2">→ {r.current_target}</span>
                  </div>
                  <Button size="sm" variant="outline"
                          data-testid={`pending-shift-confirm-${r.commitment_id}`}
                          onClick={async () => {
                            const s = r.pending_shifts[0];
                            try {
                              await api.post(`/commitments/${r.commitment_id}/confirm_shift`, { shift_id: s.id });
                              toast.success('Re-baseline confirmed.');
                              load();
                            } catch (e) { toast.error(parseErr(e)); }
                          }}>
                    <CheckCircle2 size={13} className="mr-1" />Confirm
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <MyMilestonesStrip />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="my-week-columns">
          {BUCKETS.map(b => {
            const cap = capacityFor(b.key, grouped[b.key].length);
            return (
            <div
              key={b.key}
              onDragOver={(e) => onDragOver(e, b.key)}
              onDragLeave={onDragLeave}
              onDrop={(e) => onDrop(e, b.key)}
              className={`card p-3 flex flex-col min-h-[300px] transition-colors ${dragOver === b.key ? 'ring-2' : ''}`}
              style={dragOver === b.key ? { ['--tw-ring-color']: b.color } : cap ? { borderColor: `hsl(var(--${cap.tone}))` } : {}}
              data-testid={`my-week-col-${b.key}`}
            >
              <div className="flex items-center justify-between mb-2 gap-2">
                <div className="min-w-0">
                  <div className="font-heading font-semibold text-[15px] flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full" style={{ background: b.color }} />
                    {b.label}
                  </div>
                  <div className="text-[11.5px] text-[hsl(var(--foreground-muted))] mt-0.5">{b.hint}</div>
                </div>
                <div className="flex items-center gap-1.5 flex-none">
                  {cap && (
                    <span className={`chip chip-${cap.tone}`}
                          title={cap.hint}
                          data-testid={`my-week-capacity-${b.key}`}>
                      <Flame size={10} className="inline mr-0.5" />{cap.label}
                    </span>
                  )}
                  <span className="chip tabular-nums" data-testid={`my-week-count-${b.key}`}>{grouped[b.key].length}</span>
                </div>
              </div>
              {grouped[b.key].length === 0 && (
                <div className="flex-1 flex items-center justify-center text-[12px] text-[hsl(var(--foreground-subtle))] italic border border-dashed rounded-md p-4"
                     style={{ borderColor: 'hsl(var(--border))' }}>
                  Drop a commitment here.
                </div>
              )}
              <div className="space-y-2">
                {grouped[b.key].map(c => (
                  <CommitmentCard
                    key={c.id}
                    c={c}
                    projects={projects}
                    busy={busyId === c.id}
                    isLead={isLead}
                    onDragStart={(e) => onDragStart(e, c)}
                    onShiftClick={() => setShiftFor(c)}
                    onComplete={async () => {
                      try {
                        await api.post(`/commitments/${c.id}/complete`, { actual_date: todayISO() });
                        toast.success('Marked complete.');
                        load();
                      } catch (e) { toast.error(parseErr(e)); }
                    }}
                  />
                ))}
              </div>
            </div>
          );
          })}
        </div>
      </div>

      {addOpen && meta && (
        <NewCommitmentDialog
          projects={projects}
          meta={meta}
          onClose={() => setAddOpen(false)}
          onDone={() => { setAddOpen(false); load(); }}
        />
      )}
      {shiftFor && meta && (
        <ShiftTargetDialog
          commitment={shiftFor}
          projects={projects}
          onClose={() => setShiftFor(null)}
          onDone={() => { setShiftFor(null); load(); }}
        />
      )}
    </AppShell>
  );
}

function CommitmentCard({ c, projects, busy, isLead, onDragStart, onShiftClick, onComplete }) {
  const projMap = useMemo(() => Object.fromEntries(projects.map(p => [p.id, p])), [projects]);
  const overdue = c.derived_status === 'overdue';
  const overload = !!c.overload_flag;
  const rebaselined = !!c.is_rebaselined;
  const isSelfMoved = (c.self_moves_count || 0) > 0;
  return (
    <div
      draggable
      onDragStart={onDragStart}
      className={`border rounded-md p-2.5 bg-[hsl(var(--card))] ${busy ? 'opacity-60' : 'cursor-grab active:cursor-grabbing'} hover:border-[hsl(var(--border-strong))]`}
      style={{ borderColor: overdue ? 'hsl(var(--danger))' : 'hsl(var(--border))' }}
      data-testid={`my-week-card-${c.id}`}
    >
      <div className="flex items-start gap-2">
        <Move size={12} className="mt-1 text-[hsl(var(--foreground-subtle))] flex-none" />
        <div className="flex-1 min-w-0">
          <div className="text-[13.5px] font-medium leading-snug">{c.title}</div>
          <div className="flex flex-wrap items-center gap-1.5 mt-1 text-[11.5px] text-[hsl(var(--foreground-muted))]">
            <CalendarClock size={11} />Target: <span className="tabular-nums">{c.target_date || 'unset'}</span>
            {typeof c.days_until_target === 'number' && (
              <span className={overdue ? 'text-[hsl(var(--danger))] font-medium' : ''}>
                ({c.days_until_target < 0 ? `${Math.abs(c.days_until_target)} d overdue` : c.days_until_target === 0 ? 'today' : `in ${c.days_until_target} d`})
              </span>
            )}
            {c.working_day_delay > 0 && (
              <span className="chip chip-danger" title="Working-day delay (weekends + holidays excluded)">
                +{c.working_day_delay} wd late
              </span>
            )}
            {overload && (
              <span className="chip chip-danger" data-testid={`my-week-overload-chip-${c.id}`}>
                <Flame size={10} className="inline mr-0.5" />overload
              </span>
            )}
            {isSelfMoved && !overload && (
              <span className="chip chip-warning" title="You have moved this commitment yourself.">
                <TimerReset size={10} className="inline mr-0.5" />{c.self_moves_count} self-move{c.self_moves_count === 1 ? '' : 's'}
              </span>
            )}
            {rebaselined && (
              <span className="chip chip-info" title="Target was re-baselined by a lead/owner — KRA penalty forgiven from that point." data-testid={`my-week-rebaseline-chip-${c.id}`}>
                <Flag size={10} className="inline mr-0.5" />re-baselined
              </span>
            )}
            {c.project_id && projMap[c.project_id] && (
              <><span>·</span><Layers size={11} />{projMap[c.project_id].name}</>
            )}
            {c.stage && (
              <><span>·</span><span>{STAGE_LABEL[c.stage] || c.stage}</span></>
            )}
          </div>
        </div>
        <div className="flex-none flex flex-col gap-1">
          <Button size="sm" variant="outline" className="h-7 px-2" onClick={onComplete} data-testid={`my-week-complete-${c.id}`}>
            <CheckCircle2 size={12} className="mr-1" />Done
          </Button>
          {isLead && (
            <Button size="sm" variant="ghost" className="h-7 px-2" onClick={onShiftClick} data-testid={`my-week-shift-${c.id}`}>
              <ArrowRight size={12} className="mr-1" />Shift
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function NewCommitmentDialog({ projects, meta, onClose, onDone }) {
  const [title, setTitle] = useState('');
  const [detail, setDetail] = useState('');
  const [targetDate, setTargetDate] = useState(todayISO());
  const [projectId, setProjectId] = useState('');
  const [stage, setStage] = useState('');
  const [saving, setSaving] = useState(false);
  const canSave = title.trim().length > 0 && targetDate;
  const save = async () => {
    setSaving(true);
    try {
      await api.post('/commitments', {
        title: title.trim(),
        detail: detail.trim() || null,
        target_date: targetDate,
        project_id: projectId || null,
        stage: stage || null,
      });
      toast.success('Commitment created.');
      onDone();
    } catch (e) { toast.error(parseErr(e)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="my-week-new-dialog">
        <DialogHeader>
          <DialogTitle>New commitment</DialogTitle>
          <DialogDescription>Set a target date you believe. Moving it later is a signal, not a violation.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <div className="text-[12px] text-[hsl(var(--foreground-muted))] mb-1">Title</div>
            <Input value={title} onChange={e => setTitle(e.target.value)} data-testid="my-week-new-title"
                   placeholder="e.g. Send Aravali status note to client" />
          </div>
          <div>
            <div className="text-[12px] text-[hsl(var(--foreground-muted))] mb-1">Detail (optional)</div>
            <Textarea rows={2} value={detail} onChange={e => setDetail(e.target.value)} data-testid="my-week-new-detail" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <div className="text-[12px] text-[hsl(var(--foreground-muted))] mb-1">Target date</div>
              <Input type="date" value={targetDate} onChange={e => setTargetDate(e.target.value)} data-testid="my-week-new-target" />
            </div>
            <div>
              <div className="text-[12px] text-[hsl(var(--foreground-muted))] mb-1">Project (optional)</div>
              <select className="h-9 w-full border rounded-md px-2 text-[14px]" style={{ borderColor: 'hsl(var(--border))' }}
                      value={projectId} onChange={e => setProjectId(e.target.value)} data-testid="my-week-new-project">
                <option value="">— None —</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <div className="text-[12px] text-[hsl(var(--foreground-muted))] mb-1">Stage (optional)</div>
              <select className="h-9 w-full border rounded-md px-2 text-[14px]" style={{ borderColor: 'hsl(var(--border))' }}
                      value={stage} onChange={e => setStage(e.target.value)} data-testid="my-week-new-stage">
                <option value="">— None —</option>
                {STAGES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={!canSave || saving} data-testid="my-week-new-save">
            {saving ? 'Saving…' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ShiftTargetDialog({ commitment, projects = [], onClose, onDone }) {
  const [newDate, setNewDate] = useState(commitment.target_date || todayISO());
  const [reason, setReason] = useState('');
  const [shiftedFor, setShiftedFor] = useState('');
  const [saving, setSaving] = useState(false);
  const canSave = newDate && reason.trim().length >= 3 && shiftedFor.trim().length >= 2;
  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/commitments/${commitment.id}/shift_target`, {
        new_target_date: newDate,
        reason: reason.trim(),
        shifted_for_project: shiftedFor.trim(),
        auto_confirm: true,
      });
      toast.success('Target re-baselined. KRA penalty forgiven from now.');
      onDone();
    } catch (e) { toast.error(parseErr(e)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="shift-target-dialog">
        <DialogHeader>
          <DialogTitle>Shift target date</DialogTitle>
          <DialogDescription>
            You’re re-baselining <b>&ldquo;{commitment.title}&rdquo;</b>. This forgives the KRA penalty from now.
            A short reason and the project that took priority are required — both are logged in the audit trail.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-[12.5px] text-[hsl(var(--foreground-muted))]">
            Owner: <b>{commitment.user_name || commitment.user_email}</b> · Current target: <b>{commitment.target_date}</b>
            {commitment.working_day_delay > 0 && <> · <span className="text-[hsl(var(--danger))]">+{commitment.working_day_delay} wd late</span></>}
          </div>
          <div>
            <div className="text-[12px] text-[hsl(var(--foreground-muted))] mb-1">New target date</div>
            <Input type="date" value={newDate} onChange={e => setNewDate(e.target.value)} data-testid="shift-target-date" />
          </div>
          <div>
            <div className="text-[12px] text-[hsl(var(--foreground-muted))] mb-1">Shifted for which project? (required)</div>
            <Input value={shiftedFor} onChange={e => setShiftedFor(e.target.value)}
                   list="shift-projects-list" data-testid="shift-target-for-project"
                   placeholder="e.g. Marina Bay — fabrication kickoff" />
            <datalist id="shift-projects-list">
              {projects.map(p => <option key={p.id} value={p.name} />)}
            </datalist>
          </div>
          <div>
            <div className="text-[12px] text-[hsl(var(--foreground-muted))] mb-1">Reason (required)</div>
            <Textarea rows={3} value={reason} onChange={e => setReason(e.target.value)} data-testid="shift-target-reason"
                      placeholder="e.g. Fabricator redlines added 2 days; re-baseline with Ops sign-off." />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={!canSave || saving} data-testid="shift-target-save">
            {saving ? 'Saving…' : 'Re-baseline'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
